import { useState } from 'react';
import axios from 'axios';
import { endpoints, baseUrl } from '../../../api/api';
import { Section, BookMetadata } from '../types';
import { useAlert } from '../../../components/AlertBanner';
import { trackPreviewClicked, trackPdfGenerationComplete, trackPdfGenerationFailed, trackPdfDownloaded, trackPreviewAbandoned, trackApprovePageCountFailed } from '../../../utils/analytics';

interface UsePdfGenerationProps {
  sections: Section[];
  bookSize: string;
  bookLanguage: string;
  bookTitle: string;
  bookMetadata: BookMetadata;
  bookId?: string;
  onPageCountSaved?: (pageCount: number) => void;
  onApproved?: (pageCount: number, contentHash: string) => void;
  // True when this book had no uploaded file and no real content before the
  // caller rendered this hook — i.e. the FIRST preview ever generated for it.
  // Passed in from the caller since it reflects the book's state, not
  // anything this hook tracks itself. Gates the Meta Pixel Lead event so
  // clicking "Generate Preview" repeatedly doesn't refire it.
  hadNoContentBefore?: boolean;
}

export const usePdfGeneration = ({ sections, bookSize, bookLanguage, bookTitle, bookMetadata, bookId, onPageCountSaved, onApproved, hadNoContentBefore = false }: UsePdfGenerationProps) => {
  const { showAlert } = useAlert();
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfZoom, setPdfZoom] = useState(1);
  const [pdfSource, setPdfSource] = useState<'backend' | 'legacy'>('backend');
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isApprovingPageCount, setIsApprovingPageCount] = useState(false);
  // The pageCount/contentHash pair from the most recent successful backend preview —
  // this is exactly what gets sent to /approve-page-count when the user approves it.
  const [lastPreview, setLastPreview] = useState<{ pageCount: number; contentHash: string } | null>(null);

  const generateBackendPDF = async () => {
    const startTime = Date.now();
    try {
      setIsGeneratingPdf(true);

      const nonEmptySections = sections.filter(s => s.content && s.content.trim() !== '' && s.content !== '<p></p>');

      if (nonEmptySections.length === 0) {
        showAlert('No content to generate PDF. Please add some content to your sections.', 'warning');
        return;
      }

      const sectionsForPdf = nonEmptySections.map(s => ({
        id: s.id,
        title: s.title,
        content: s.content,
        type: s.type || 'chapter'
      }));

      const response = await axios.post(`${baseUrl}${endpoints.previewPDF}`, {
        sections: sectionsForPdf,
        bookTitle: bookTitle || 'Book',
        bookMetadata: bookMetadata,
        bookSize: bookSize,
        language: bookLanguage
      });

      if (response.data.success && response.data.pdf) {
        const cleanBase64 = response.data.pdf.replace(/\s/g, '');
        const binaryString = await fetch(`data:application/pdf;base64,${cleanBase64}`);
        const pdfBlob = await binaryString.blob();
        const url = URL.createObjectURL(pdfBlob);

        setPdfUrl(url);
        setPdfSource('backend');
        setShowPdfModal(true);

        const count = response.data.pageCount;
        trackPdfGenerationComplete(count || 0, Date.now() - startTime, hadNoContentBefore);
        if (count && response.data.contentHash) {
          setLastPreview({ pageCount: count, contentHash: response.data.contentHash });
        }
        if (count && bookId) {
          const token = localStorage.getItem('accessToken');
          axios.patch(
            `${baseUrl}${endpoints.updatePageCount.replace(':id', bookId)}`,
            { pageCount: count },
            { headers: { Authorization: `Bearer ${token}` } },
          ).then(() => onPageCountSaved?.(count)).catch(() => {});
        }
      } else {
        throw new Error(response.data.message || 'Failed to generate PDF');
      }
    } catch (error: any) {
      const status = error.response?.status;
      const errMsg = error.response?.data?.message || error.message || 'Unknown error';
      console.error('Error generating backend PDF:', error);
      trackPdfGenerationFailed(errMsg, {
        book_id: bookId,
        book_title: bookTitle,
        section_count: sections.length,
        generator: 'backend',
      });
      if (status === 429 || status === 503) {
        showAlert('PDF preview is under high demand right now — please try again in a moment.', 'warning');
      } else {
        showAlert(`Failed to generate PDF: ${errMsg}`, 'error');
      }
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const generateLegacyPDF = async () => {
    const startTime = Date.now();
    try {
      setIsGeneratingPdf(true);

      const nonEmptySections = sections.filter(s => s.content && s.content.trim() !== '' && s.content !== '<p></p>');

      if (nonEmptySections.length === 0) {
        showAlert('No content to generate PDF. Please add some content to your sections.', 'warning');
        return;
      }

      const sectionsForApi = nonEmptySections.map(s => ({
        id: s.id,
        title: s.title,
        content: s.content,
        type: s.type || 'chapter'
      }));

      const response = await axios.post(endpoints.reactPdfPreview, {
        sections: sectionsForApi,
        bookTitle: bookTitle || 'Book Preview',
        bookSize: bookSize,
        language: bookLanguage,
        bookMetadata: bookMetadata
      });

      if (response.data.success && response.data.pdf) {
        const binaryString = atob(response.data.pdf);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const pdfBlob = new Blob([bytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(pdfBlob);

        setPdfUrl(url);
        setPdfSource('legacy');
        setShowPdfModal(true);

        const count = response.data.pageCount;
        trackPdfGenerationComplete(count || 0, Date.now() - startTime, hadNoContentBefore);
        if (count && bookId) {
          const token = localStorage.getItem('accessToken');
          axios.patch(
            `${baseUrl}${endpoints.updatePageCount.replace(':id', bookId)}`,
            { pageCount: count },
            { headers: { Authorization: `Bearer ${token}` } },
          ).then(() => onPageCountSaved?.(count)).catch(() => {});
        }
      } else {
        throw new Error(response.data.message || 'Failed to generate PDF');
      }
    } catch (error: any) {
      const errMsg = error.response?.data?.message || error.message || 'Unknown error';
      console.error('Error generating legacy PDF:', error);
      trackPdfGenerationFailed(errMsg, {
        book_id: bookId,
        book_title: bookTitle,
        section_count: sections.length,
        generator: 'legacy',
      });
      showAlert(`Failed to generate legacy PDF: ${errMsg}`, 'error');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const closePdfModal = () => {
    trackPreviewAbandoned();
    setShowPdfModal(false);
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  const downloadPDF = () => {
    if (pdfUrl) {
      const a = document.createElement('a');
      a.href = pdfUrl;
      a.download = `book-preview-${pdfSource}.pdf`;
      a.click();
      trackPdfDownloaded();
    }
  };

  const handleZoomIn = () => setPdfZoom(Math.min(2, pdfZoom + 0.25));
  const handleZoomOut = () => setPdfZoom(Math.max(0.5, pdfZoom - 0.25));

  // Explicitly approves the most recently generated backend preview's page count —
  // this becomes the source of truth for spine width, distinct from just having
  // viewed a preview. Requires a preview to have been generated in this session.
  // Returns whether it succeeded, so callers can decide whether it's safe to
  // proceed to the next step (e.g. "Confirm and Continue").
  const approvePageCount = async (): Promise<boolean> => {
    if (!lastPreview || !bookId) return false;

    setIsApprovingPageCount(true);
    try {
      const token = localStorage.getItem('accessToken');
      await axios.patch(
        `${baseUrl}${endpoints.approvePageCount.replace(':id', bookId)}`,
        { pageCount: lastPreview.pageCount, contentHash: lastPreview.contentHash },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      onApproved?.(lastPreview.pageCount, lastPreview.contentHash);
      return true;
    } catch (error: any) {
      const errMsg = error.response?.data?.message || error.message || 'Unknown error';
      trackApprovePageCountFailed(errMsg);
      showAlert(`Failed to approve page count: ${errMsg}`, 'error');
      return false;
    } finally {
      setIsApprovingPageCount(false);
    }
  };

  const generatePdf = () => {
    trackPreviewClicked(bookTitle || 'Book');
    if (bookLanguage === "English") {
      generateBackendPDF();
    } else {
      generateBackendPDF();
    }
  };

  return {
    showPdfModal,
    pdfUrl,
    pdfZoom,
    pdfSource,
    isGeneratingPdf,
    generatePdf,
    closePdfModal,
    downloadPDF,
    handleZoomIn,
    handleZoomOut,
    lastPreview,
    isApprovingPageCount,
    approvePageCount
  };
};
